import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, doc, getFirestore, getDoc, getDocs, query, where } from "firebase/firestore";
import AdminShell from "../../admin/AdminShell";
import {useSession} from "../../../context/SessionContext";
import app from "../../../firebase";
import { updateManagedUser } from "../../../services/updateManagedUser";

const getTextValue = value => {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (value && typeof value === 'object') {
    return getTextValue(value.name || value.label || value.title || value.value || value.department || value.program || value.school);
  }
  return '';
};

export default function EditUser({ user, onClose, onSaved }) {
  const navigate = useNavigate();
  const {currentUser} = useSession();
  const { userId } = useParams();
  const [localUser, setLocalUser] = useState(user || null);
  const [form, setForm] = useState({ role: '', firstName: '', middleName: '', lastName: '', school: '', department: '', email: '', program: '', status: 'Active' });
  const [loading, setLoading] = useState(!user && !!userId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [schoolError, setSchoolError] = useState('');
  const [departmentError, setDepartmentError] = useState('');

  const isPage = !onClose;
  const recordId = (user && (user.uid || user.id)) || userId;
  const isStudentRecord = userId?.startsWith('students_');
  const documentId = isStudentRecord ? userId.replace(/^students_/, '') : userId;
  const documentCollection = isStudentRecord ? 'students' : 'users';

  useEffect(() => {
    let active = true;
    const fetchSchools = async () => {
      setLoadingSchools(true);
      setSchoolError('');
      try {
        const snapshot = await getDocs(collection(getFirestore(app), 'schools'));
        if (active) {
          const schoolNames = snapshot.docs.map(item => getTextValue(item.data()?.name)).filter(Boolean);
          setSchools([...new Set(schoolNames)].sort((a, b) => a.localeCompare(b)));
        }
      } catch (err) {
        console.error('Failed to load schools', err);
        if (active) setSchoolError('Failed to load schools');
      } finally {
        if (active) setLoadingSchools(false);
      }
    };
    fetchSchools();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!form.school) {
      setDepartments([]);
      setLoadingDepartments(false);
      setDepartmentError('');
      return () => {
        active = false;
      };
    }

    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      setDepartmentError('');
      try {
        const db = getFirestore(app);
        const snapshot = await getDocs(query(collection(db, 'departments'), where('school', '==', form.school)));
        if (active) {
          const departmentNames = snapshot.docs.map(item => getTextValue(item.data()?.name)).filter(Boolean);
          setDepartments([...new Set(departmentNames)].sort((a, b) => a.localeCompare(b)));
        }
      } catch (err) {
        console.error('Failed to load departments', err);
        if (active) setDepartmentError('Failed to load departments');
      } finally {
        if (active) setLoadingDepartments(false);
      }
    };

    fetchDepartments();
    return () => {
      active = false;
    };
  }, [form.school]);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const db = getFirestore(app);
        const ref = doc(db, documentCollection, documentId);
        const snap = await getDoc(ref);
        if (snap.exists()) setLocalUser({ id: snap.id, ...snap.data() });
        else setError('User not found');
      } catch (err) {
        console.error(err);
        setError(err?.message || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    if (!user && userId) fetchUser();
  }, [user, userId]);

  useEffect(() => {
    if (localUser) {
      setForm({
        role: localUser.role || '',
        firstName: localUser.firstName || '',
        middleName: localUser.middleName || '',
        lastName: localUser.lastName || '',
        school: getTextValue(localUser.school),
        department: getTextValue(localUser.department),
        email: getTextValue(localUser.email),
        program: getTextValue(localUser.program),
        status: getTextValue(localUser.status) || 'Active'
      });
    }
  }, [localUser]);

  const handleChange = (k, v) => setForm(prev => ({
    ...prev,
    [k]: v,
    ...(k === 'school' ? { department: '' } : {})
  }));
  const showSchoolDepartment = ['Student', 'Dean', 'Program Head', 'Professor'].includes(form.role);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // validate before saving
      const validateFields = () => {
        const errors = {};
        const nameRegex = /^[A-Za-z\s'-]+$/;

        if (!form.firstName || form.firstName.trim().length === 0) {
          errors.firstName = 'First name is required';
        } else if (!nameRegex.test(form.firstName)) {
          errors.firstName = 'First name must contain only letters';
        } else if (form.firstName.trim().length > 15) {
          errors.firstName = 'Maximum 15 characters';
        }

        if (!form.lastName || form.lastName.trim().length === 0) {
          errors.lastName = 'Last name is required';
        } else if (!nameRegex.test(form.lastName)) {
          errors.lastName = 'Last name must contain only letters';
        } else if (form.lastName.trim().length > 15) {
          errors.lastName = 'Maximum 15 characters';
        }

        if (form.middleName && form.middleName.trim().length > 0) {
          if (!nameRegex.test(form.middleName)) {
            errors.middleName = 'Middle name must contain only letters';
          } else if (form.middleName.trim().length > 15) {
            errors.middleName = 'Maximum 15 characters';
          }
        }

        const emailVal = (form.email || '').trim();
        if (!emailVal) {
          errors.email = 'Email is required';
        } else if (emailVal.length < 3 || emailVal.length > 25) {
          errors.email = 'Email must be between 3 and 25 characters';
        } else if (!emailVal.includes('@') || !emailVal.toLowerCase().includes('.com')) {
          errors.email = 'Email must contain "@" and ".com"';
        }

        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
          setError('Please fix the highlighted fields');
          return false;
        }
        setError('');
        return true;
      };

      if (!validateFields()) {
        return;
      }
      if (!recordId) throw new Error('User ID is missing');
      const displayName = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ').trim();
      await updateManagedUser({
        caller: currentUser,
        collectionName: documentCollection,
        documentId,
        accountUid: localUser.uid || (documentCollection === 'users' ? documentId : ''),
        data: {
          role: form.role || null,
          firstName: form.firstName || null,
          middleName: form.middleName || null,
          lastName: form.lastName || null,
          fullName: displayName || null,
          name: displayName || null,
          school: form.school || null,
          department: form.department || null,
          email: form.email || null,
          program: form.program || null,
          status: form.status || null
        }
      });
      if (onSaved) onSaved();
      if (onClose) return onClose();
      navigate('/admin/users');
    } catch (err) {
      console.error('EditUser failed', err);
      if (err?.code === 'email-already-exists' || err?.code === 'invalid-email') {
        setFieldErrors(current => ({ ...current, email: err.message }));
      }
      setError(err?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div style={{ width: isPage ? '100%' : 720, maxWidth: isPage ? 900 : '96%', background: '#fff', borderRadius: 10, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, color: '#155c2b' }}>Edit User</h3>
        {onClose ? (
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
        ) : (
          <button onClick={() => navigate('/admin/users')} style={{ background: '#fff', border: '1px solid #dfe7e3', color: '#155c2b', padding: '8px 12px', borderRadius: 8, cursor: 'pointer' }}>Back to Users</button>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        {loading && <div>Loading user...</div>}
        {!loading && !localUser && <div style={{ color: 'crimson' }}>{error || 'No user selected.'}</div>}
        {!loading && localUser && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontWeight: 700 }}>Role</label>
                <select value={form.role} onChange={e => handleChange('role', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: '1px solid #e6e9ec' }}>
                  <option value="">Select role</option>
                  <option>Student</option>
                  <option>Professor</option>
                  <option>Dean</option>
                  <option>Program Head</option>
                  <option>Registrar</option>
                  <option>Admission Counselor</option>
                  <option>Room Manager</option>
                  <option>System Administrator</option>
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 700 }}>First Name</label>
                <input value={form.firstName} onChange={e => handleChange('firstName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.firstName ? '1px solid crimson' : '1px solid #e6e9ec' }} />
                {fieldErrors.firstName && <div style={{ color: 'crimson', marginTop: 6 }}>{fieldErrors.firstName}</div>}
              </div>
              <div>
                <label style={{ fontWeight: 700 }}>Middle Name</label>
                <input value={form.middleName} onChange={e => handleChange('middleName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.middleName ? '1px solid crimson' : '1px solid #e6e9ec' }} />
                {fieldErrors.middleName && <div style={{ color: 'crimson', marginTop: 6 }}>{fieldErrors.middleName}</div>}
              </div>
              <div>
                <label style={{ fontWeight: 700 }}>Last Name</label>
                <input value={form.lastName} onChange={e => handleChange('lastName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.lastName ? '1px solid crimson' : '1px solid #e6e9ec' }} />
                {fieldErrors.lastName && <div style={{ color: 'crimson', marginTop: 6 }}>{fieldErrors.lastName}</div>}
              </div>
              {showSchoolDepartment && (
                <>
                  <div>
                    <label style={{ fontWeight: 700 }}>School of</label>
                    <select value={form.school} onChange={e => handleChange('school', e.target.value)} disabled={loadingSchools} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.school ? '1px solid crimson' : '1px solid #e6e9ec' }}>
                      <option value="">{loadingSchools ? 'Loading schools...' : 'Select school'}</option>
                      {form.school && !schools.includes(form.school) && <option value={form.school}>{form.school}</option>}
                      {schools.map(school => <option key={school} value={school}>{school}</option>)}
                    </select>
                    {fieldErrors.school && <div style={{ color: 'crimson', marginTop: 6 }}>{fieldErrors.school}</div>}
                    {schoolError && <div style={{ color: 'crimson', marginTop: 6 }}>{schoolError}</div>}
                  </div>
                  <div>
                    <label style={{ fontWeight: 700 }}>Department</label>
                    <select value={form.department} onChange={e => handleChange('department', e.target.value)} disabled={!form.school || loadingDepartments} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.department ? '1px solid crimson' : '1px solid #e6e9ec' }}>
                      <option value="">{form.school ? (loadingDepartments ? 'Loading departments...' : 'Select department') : 'Choose a school first'}</option>
                      {form.department && !departments.includes(form.department) && <option value={form.department}>{form.department}</option>}
                      {departments.map(department => <option key={department} value={department}>{department}</option>)}
                    </select>
                    {fieldErrors.department && <div style={{ color: 'crimson', marginTop: 6 }}>{fieldErrors.department}</div>}
                    {departmentError && <div style={{ color: 'crimson', marginTop: 6 }}>{departmentError}</div>}
                  </div>
                </>
              )}
              <div>
                <label style={{ fontWeight: 700 }}>Email</label>
                <input value={form.email} onChange={e => handleChange('email', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.email ? '1px solid crimson' : '1px solid #e6e9ec' }} />
                {fieldErrors.email && <div style={{ color: 'crimson', marginTop: 6 }}>{fieldErrors.email}</div>}
              </div>
              <div>
                <label style={{ fontWeight: 700 }}>Status</label>
                <select value={form.status} onChange={e => handleChange('status', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: '1px solid #e6e9ec' }}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>

            {error && error !== fieldErrors.email && <div style={{ color: 'crimson', marginTop: 12 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              {onClose && <button onClick={onClose} style={{ background: '#fff', border: '1px solid #ddd', padding: '10px 14px', borderRadius: 8 }}>Cancel</button>}
              <button onClick={handleSave} disabled={saving} style={{ background: '#155c2b', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (isPage) {
    return (
      <AdminShell title="Edit User">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>{content}</div>
      </AdminShell>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      {content}
    </div>
  );
}
